/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import Raven from 'raven-js';
import get from 'lodash/get';

import getUrl from '@/routes/routes/helpers/getUrl';

import trackingService from '@services/tracking';
import {
  UserQueryPrivate,
  AssociateGuestContributionIdsMutation,
} from '@services/gql/user';
import { captureEmailAndSetBrazeUser } from '@services/braze';
import { AssociateGuestBookingIdsMutation } from '@services/gql/booking';

import { EmailAcquisitionChannel } from '@/enums';

import {
  trackAccountLogin,
  trackAccountRegistrationLogin,
  trackPageVisit,
  PAGE_NAME,
} from '@utils/tracking';
import {
  getGuestBookingIds,
  removeGuestBookingIds,
} from '@utils/guestBookingStorage';
import {
  getGuestContributionIds,
  removeGuestContributionIds,
} from '@utils/guestContributionStorage';
import { getObjFromHashParamString } from '@utils/urls';

const associateGuestContributionIdsWithUser = async (client) => {
  const guestContributionIds = getGuestContributionIds();
  if (guestContributionIds.length === 0) return;

  const { errors } = await client.mutate({
    mutation: AssociateGuestContributionIdsMutation,
    variables: { ids: guestContributionIds },
  });
  if (!errors) {
    removeGuestContributionIds(guestContributionIds);
  }
};

const associateGuestBookingIdsWithUser = async (client) => {
  const guestBookingIds = getGuestBookingIds();
  if (guestBookingIds.length === 0) return;

  const { data, errors } = await client.mutate({
    mutation: AssociateGuestBookingIdsMutation,
    variables: { input: { ids: guestBookingIds } },
  });

  const successfullyAddedIds = get(data, 'associateGuestBookingIds', [])
    .map((updatedBooking) => get(updatedBooking, 'id', false))
    .filter((el) => el);

  if (!errors && successfullyAddedIds.length > 0) {
    removeGuestBookingIds(successfullyAddedIds);
  }
};

const trackUser = async (profile, source, returnUrl) => {
  if (profile?.loginsCount === 1) {
    trackAccountRegistrationLogin(profile, returnUrl, source);
  } else {
    trackAccountLogin(profile, returnUrl);
  }
  return trackingService.identifyUser(profile);
};

const getPrivateUserData = async (primaryIdentityId, client) => {
  await client.resetStore();
  const { data, errors } = await client.query({
    query: UserQueryPrivate,
    variables: { id: primaryIdentityId },
  });

  if (!data?.profile || errors) throw Error('UserQueryPrivate failed');
  return data?.profile;
};

// TODO: check if settings can be removed and just redirect where the user wanted
const redirect = (returnUrl, settings, interests, router, location) => {
  if (returnUrl === '/profile' && !(settings && settings.updatedAt)) {
    router.push('/profile/settings');
  } else if (returnUrl === '/' && !interests) {
    router.push(
      getUrl.onboarding({
        source: location.query.source,
      }),
    );
  } else {
    router.push(returnUrl);
  }
};

const handleError = (error) => {
  Raven.captureException(error);
};

const getUserProfile = async (primaryIdentityId, client) => {
  try {
    const profile = await getPrivateUserData(primaryIdentityId, client);
    return profile;
  } catch (error) {
    handleError(error);
    return {};
  }
};

const handlePostLoginActions = async ({
  client,
  router,
  location,
  authService,
  noRedirect,
}) => {
  const primaryIdentityId = authService?.getPrimaryIdentityId?.();
  const returnUrl = authService?.getReturnUrl?.();

  trackPageVisit(PAGE_NAME.LOGIN_CALLBACK_PAGE, 0);

  const profile = await getUserProfile(primaryIdentityId, client);
  const emailAddress = profile?.emailAddress;

  try {
    const source = getObjFromHashParamString(location?.hash)?.source;
    await trackUser(profile, source, returnUrl);
  } catch (error) {
    handleError(error);
  }

  try {
    if (profile?.primaryIdentityId && emailAddress) {
      await captureEmailAndSetBrazeUser({
        emailAddress,
        acquisitionChannel: EmailAcquisitionChannel.LOGIN,
        ...profile,
        authService,
      });
    }
  } catch (error) {
    handleError(error);
  }

  try {
    await associateGuestBookingIdsWithUser(client);
  } catch (error) {
    handleError(error);
  }

  try {
    await associateGuestContributionIdsWithUser(client);
  } catch (error) {
    handleError(error);
  }

  if (noRedirect) return;
  // TODO: check if profile.setting can be removed
  redirect(returnUrl, profile?.settings, profile?.interests, router, location);
};

export default handlePostLoginActions;
